/**
 * 「课文」界面的**总开关**（暂时隐藏，数据模型与导入解析器全部保留）。
 *
 * ## 用户原话（2026-10-17）
 *
 * > 2 和 3：暂时不需要录课文，可以先隐藏和关闭这个功能。
 *
 * 现场是：「听写字表 → 浏览与编辑」页里那张「课本与课文」卡片一直是空的
 * （"这个筛选下还没有课文"），白占一块位置；筛选区里的「课文」下拉也跟着没意义。
 *
 * ## ⛔ 这个开关**只**管界面，不碰数据
 *
 * 关掉的是**渲染**（卡片 / 下拉 / 编辑弹窗里的"关联课文"），**不是**能力：
 *   · `lesson` / `hanzi_lesson` / `word_lesson` 三张表、它们的接口
 *     （`/admin/dictation/lessons` 等 21 条维护端点里的课文那几条）**一条没删**；
 *   · 导入解析器（`packages/shared/src/dictation/import-parser.ts`）**照旧接受
 *     「课文」列**：粘一份带课文列的文本，课文行仍然会被解析并落库
 *     （`contextOnly` 行 → 建课 + 关联），这条有测试守着；
 *   · 组件（`DictationLessonForm` / 卡片 / 下拉）**都在原处**，只是不渲染。
 *
 * ## 🔁 恢复办法：把下面这一行改成 `true`
 *
 * 改完这一处，以下界面会一起回来（它们全部读这个常量，没有第二处开关）：
 *   · 「浏览与编辑」筛选区的「课文」下拉；
 *   · 「课本与课文」卡片（新建 / 编辑 / 删除课文、只看这一课）；
 *   · 生字/词语编辑弹窗里的「关联课文」勾选区（以及提交 `lessonIds`）；
 *   · tab 副标题里的"课文"字样；
 *   · 筛选区列数（隐藏时收成 4 列，免得最后空一格）。
 *
 * 这就是为什么这里不做"物理删除"：恢复只该是一次一行的改动。
 */
export const DICTATION_LESSONS_UI_ENABLED = false;

/**
 * 界面现在要不要渲染「课文」相关的东西。
 *
 * 参数只有一个用途：**单测**要能验证开关两个方向的行为（`true` / `false`），
 * 而生产调用处一律不传参（用上面的常量）。
 */
export function lessonsUiEnabled(
  enabled: boolean = DICTATION_LESSONS_UI_ENABLED,
): boolean {
  return enabled;
}

/**
 * 「浏览与编辑」筛选区里下拉的**顺序**（出版社 → 年级 → 学期 → 课文 → 在哪张表上）。
 *
 * ⚠️ 2026-10-18：第一格从「课本版本」（`textbook`）改成「出版社」（`publisher`）——
 *    下拉里只列去重后的出版社，年级 / 学期 / 表仍然各占右边一格。
 *
 * ⚠️ 用"有序 key 列表 + 逐 key 渲染"而不是在 JSX 里手写一排 `<Select>`：
 *    隐藏「课文」只要从这里去掉一个 key，**列数**与**渲染**同时对上，
 *    不会出现"少了一列但 grid 还是 5 列"的错位。
 */
export const BROWSE_FILTER_ORDER = [
  'publisher',
  'grade',
  'semester',
  'lesson',
  'listType',
] as const;

export type BrowseFilterKey = (typeof BROWSE_FILTER_ORDER)[number];

/** 这次要渲染哪些筛选（`lessonsEnabled=true` 时才有 `lesson`）。 */
export function browseFilterKeys(
  lessonsEnabled: boolean = DICTATION_LESSONS_UI_ENABLED,
): BrowseFilterKey[] {
  return BROWSE_FILTER_ORDER.filter((key) => key !== 'lesson' || lessonsEnabled);
}

/**
 * 筛选区在大屏下的列数样式。
 *
 * 隐藏「课文」后剩 4 个下拉：若还按 5 列排，最后会**空一格**（看起来像少了个控件）。
 * ⚠️ 这两个类名必须写成字面量，Tailwind 才能在扫描时把它们收进产物。
 */
export function browseFilterGridClass(count: number): string {
  return count >= 5 ? 'lg:grid-cols-5' : 'lg:grid-cols-4';
}
